import {useEffect, useRef, useState} from 'react';
import {Link} from 'react-router';
import config from '../config';
import {RESET_MESSAGES, requestPasswordReset, validateForgot} from '../auth/passwordReset';
import AuthLayout from '../components/auth/AuthLayout';
import useIdempotencyKeys from '../hooks/useIdempotencyKeys';
import './Login.css';

const STATUS_ID = 'forgot-status';
const EMAIL_ERROR_ID = 'forgot-email-error';

/**
 * First-party password recovery: asks for the account email and sends a single-use reset link
 * (POST /api/v1/auth/password/forgot). The reply is the same whether or not the account exists.
 */
export default function ForgetPassword() {
  const keys = useIdempotencyKeys();
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [status, setStatus] = useState(null); // {kind: 'sent' | 'error', message}
  const [busy, setBusy] = useState(false);
  const emailRef = useRef(null);
  const statusRef = useRef(null);

  // Focus moves to the outcome so screen readers announce it straight after submit.
  useEffect(() => {
    if (status && statusRef.current) statusRef.current.focus();
  }, [status]);

  const submit = async (event) => {
    event.preventDefault();
    if (busy) return;
    const errors = validateForgot({email});
    if (errors.email) {
      setStatus(null);
      setEmailError(errors.email);
      if (emailRef.current) emailRef.current.focus();
      return;
    }
    setEmailError('');
    setStatus(null);
    setBusy(true);
    try {
      const address = email.trim();
      await keys.run('forgot-password', {email: address}, (idempotencyKey) =>
        requestPasswordReset(address, {idempotencyKey}),
      );
      setStatus({kind: 'sent', message: RESET_MESSAGES.sent});
    } catch (err) {
      if (err.fieldErrors && err.fieldErrors.email) {
        setEmailError(err.fieldErrors.email);
        if (emailRef.current) emailRef.current.focus();
      } else {
        setStatus({kind: 'error', message: err.message || RESET_MESSAGES.unavailable});
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout>
      <div className="auth-heading auth-heading-tight">
        <p className="auth-eyebrow">ACCOUNT</p>
        <h1 className="auth-title">Reset your password.</h1>
        <p className="auth-subtitle">Enter the email you use for AIDentalBook and we will send you a link.</p>
      </div>

      <div className="auth-panel">
        <form onSubmit={submit} noValidate aria-label="Reset your password">
          <div className="auth-fields">
            <div className="auth-field">
              <label htmlFor="forgot-email" className="auth-label">
                Email Address
              </label>
              <input
                ref={emailRef}
                id="forgot-email"
                name="email"
                type="email"
                className="auth-input"
                placeholder="name@example.com"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                aria-invalid={emailError ? 'true' : undefined}
                aria-describedby={emailError ? EMAIL_ERROR_ID : undefined}
                data-testid="forgot-email"
              />
              {emailError && (
                <p id={EMAIL_ERROR_ID} className="auth-field-error">
                  {emailError}
                </p>
              )}
            </div>
          </div>

          <div
            id={STATUS_ID}
            ref={statusRef}
            tabIndex={-1}
            role={status && status.kind === 'error' ? 'alert' : 'status'}
            aria-live={status && status.kind === 'error' ? 'assertive' : 'polite'}
            aria-atomic="true"
            className={status && status.kind === 'sent' ? 'auth-notice' : 'auth-alert'}
            data-testid="forgot-status"
          >
            {status ? status.message : ''}
          </div>

          <button type="submit" className="auth-submit" disabled={busy} data-testid="forgot-submit">
            {busy ? 'Sending…' : 'Send reset link'}
          </button>
        </form>

        {config.authentikRecoveryUrl && (
          <p className="auth-links">
            Signed in through the identity provider?{' '}
            <a className="auth-link" href={config.authentikRecoveryUrl}>
              Reset your password there
            </a>
          </p>
        )}

        <p className="auth-switch">
          <Link to="/login" className="auth-link">
            Back to sign in
          </Link>
        </p>
      </div>
    </AuthLayout>
  );
}
